import LogoGradientIcon from '../../icons/LogoGradientIcon'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectUser, setSession } from '../../reducers/users'
import { SessionState } from '../../types/UserTypes'
import { IconChevronRight, IconDots, IconEdit, IconTrash } from '@tabler/icons-react'
import {
  fetchDeviceDetails,
  removeDevices,
  selectDevices,
  setDeviceDetailsScreen,
  updateDeviceName,
} from '../../reducers/devices'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { SegmentedControl, Skeleton } from '@radix-ui/themes'
import DeviceRemoveModal from '../devices/DeviceRemoveModal'
import { useAppDispatch } from '../../reducers/store'
import toast from 'react-hot-toast'
import DeviceRenameModal from '../devices/DeviceRenameModal'
import { inPov } from '../../utils/session'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'

interface IDevicesHeaderProps {
  pageTitle: string
}

const DevicesHeader = (props: IDevicesHeaderProps) => {
  const { pageTitle } = props
  const di = useAppDispatch()
  const { deviceId } = useParams()

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { session, currentPage } = useSelector(selectUser)
  const { deviceDetails, deviceDetailsScreen } = useSelector(selectDevices)
  const isDetailPage = /^\/devices\/[^/]+\/?$/.test(location.pathname)

  const [showRemoveDeviceModal, setShowRemoveDeviceModal] = useState<boolean>(false)
  const [showRenameDeviceModal, setShowRenameDeviceModal] = useState<boolean>(false)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const tab = queryParams.get('tab')
    if (tab) {
      if (tab === 'events' && deviceDetailsScreen !== 'events') {
        dispatch(setDeviceDetailsScreen('events'))
      } else if (tab === 'forensics' && deviceDetailsScreen !== 'forensics') {
        dispatch(setDeviceDetailsScreen('forensics'))
      } else if (tab === 'scans' && deviceDetailsScreen !== 'scans') {
        dispatch(setDeviceDetailsScreen('scans'))
      } else if (tab === 'overview' && deviceDetailsScreen !== 'overview') {
        dispatch(setDeviceDetailsScreen('overview'))
      }
    }
  }, [])

  useEffect(() => {
    const goToConfigured = localStorage.getItem('SSO_CONFIGURED_COOKIE')

    if (goToConfigured === 'true') {
      navigate('/settings')
    }
    if (window.location.search.includes('error=sso-unauthorized')) {
      navigate('/sso-error')
    }
  }, [])

  useEffect(() => {
    const storedSession = localStorage.getItem('session') as string

    if (currentPage === 'SSO_ERROR') {
      localStorage.setItem('session', '{}')
      localStorage.removeItem('SSO_CONFIGURED_COOKIE')
      dispatch(setSession(undefined))
    } else if (!session && storedSession === '{}' && currentPage !== 'SSO_ERROR') {
      localStorage.setItem('session', '{}')
      localStorage.removeItem('SSO_CONFIGURED_COOKIE')
      dispatch(setSession(undefined))
      navigate('/login')
    } else if (!session && storedSession !== '{}') {
      dispatch(setSession(JSON.parse(storedSession) as SessionState))
    }
  }, [session])

  useEffect(() => {
    if (import.meta.env.VITE_PRETTY_ENV !== 'Production' && import.meta.env.VITE_ON_PREM === 'false') {
      document.title = '[' + import.meta.env.VITE_PRETTY_ENV + '] ' + 'iVerify EDR Portal'
    }
  }, [])

  const removeUserDevices = async () => {
    const removedSuccessfully = await di(removeDevices([deviceDetails!.accessCode]))
    if (removedSuccessfully) {
      setShowRemoveDeviceModal(false)
      toast.success(`Device was successfully removed`, DEFAULT_TOAST_CONFIG)
      navigate('/devices')
    } else {
      toast.error('An error occurred while removing the device(s)', DEFAULT_TOAST_CONFIG)
    }
  }

  const updateDeviceScreen = (newValue: string) => {
    dispatch(setDeviceDetailsScreen(newValue))
    const queryParams = new URLSearchParams(location.search)
    queryParams.set('tab', newValue)
    navigate(`/devices/${deviceId}?${queryParams.toString()}`, { replace: true })
  }

  const renameUserDevice = async (newName: string) => {
    const updated = await di(updateDeviceName(deviceDetails!.accessCode, newName))
    if (updated) {
      await di(fetchDeviceDetails(deviceId!))
      setShowRenameDeviceModal(false)
      toast.success(`Device was successfully renamed`, DEFAULT_TOAST_CONFIG)
    } else {
      toast.error('An error occurred while renaming the device', DEFAULT_TOAST_CONFIG)
    }
  }

  return (
    <>
      <div className="w-full absolute h-[74px] flex items-center px-6" style={{ top: inPov(session) ? '42px' : '0px' }}>
        <div className="flex items-center min-w-0 flex-1">
          <div className="h-[26px] w-[182px] shrink-0">
            <LogoGradientIcon height="26" width="71" />
          </div>
          <div className="ml-4 flex items-center overflow-hidden whitespace-nowrap text-ellipsis">
            {!isDetailPage && <div className="font-semibold text-mainText text-xl">{pageTitle}</div>}
            {isDetailPage && (
              <>
                <div
                  className="font-semibold text-neutral-11 dark:text-neutral-9 text-xl cursor-pointer shrink-0"
                  onClick={() => navigate('/devices')}
                >
                  {pageTitle}
                </div>
                <IconChevronRight size={18} className="text-neutral-11 dark:text-neutral-9 mx-1.5 shrink-0" />
                <div className="font-semibold text-xl text-mainText truncate max-w-[400px]">
                  {deviceDetails?.deviceName ?? deviceDetails?.name}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 flex xl:justify-center">
          {isDetailPage && (
            <>
              {deviceDetails == null || deviceDetails == undefined || deviceDetails.identifier !== deviceId ? (
                <Skeleton className="h-[38px] w-[300px]" />
              ) : (
                <SegmentedControl.Root
                  value={deviceDetailsScreen}
                  defaultValue="overview"
                  className="h-[38px] bg-controllerBackground text-controllerText"
                  onValueChange={(newValue: string) => updateDeviceScreen(newValue)}
                >
                  <SegmentedControl.Item value="overview">Overview</SegmentedControl.Item>
                  <SegmentedControl.Item value="scans">Scans</SegmentedControl.Item>
                  <SegmentedControl.Item value="events">Events</SegmentedControl.Item>
                  {deviceDetails?.platform === 'IOS' && (
                    <SegmentedControl.Item value="forensics">Forensics</SegmentedControl.Item>
                  )}
                </SegmentedControl.Root>
              )}
            </>
          )}
        </div>

        {isDetailPage && (
          <div className="flex items-center justify-end lg:flex-1">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <div className="h-8 px-2 flex items-center border border-borderGrey rounded-lg cursor-pointer">
                  <IconDots size={20} />
                </div>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="bg-white dark:bg-contentBackground border border-borderGrey dark:border-gray-300 min-w-[200px] rounded-xl p-2 mr-6 mt-1 shadow-lg"
                  sideOffset={5}
                >
                  <DropdownMenu.Item
                    className="flex text-mainText mb-2 text-sm cursor-pointer hover:bg-cardFill rounded-lg p-2 outline-none focus:bg-gray-100 dark:focus:bg-gray-800"
                    onClick={() => setShowRenameDeviceModal(true)}
                  >
                    <IconEdit size={20} className="mr-2 mt-0.5" />
                    <span className="mt-0.5"> Rename Device</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex text-red-500 text-sm cursor-pointer hover:bg-cardFill rounded-lg p-2 outline-none focus:bg-gray-100 dark:focus:bg-gray-800"
                    onClick={() => setShowRemoveDeviceModal(true)}
                  >
                    <IconTrash size={20} className="mr-2 mt-0.5" /> <span className="mt-0.5"> Remove Device</span>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        )}
      </div>
      {showRemoveDeviceModal && deviceDetails && (
        <DeviceRemoveModal
          clickedInputs={[deviceDetails]}
          setShowRemoveDeviceModal={setShowRemoveDeviceModal}
          removeUserDevices={removeUserDevices}
        />
      )}
      {showRenameDeviceModal && (
        <DeviceRenameModal
          oldName={deviceDetails!.deviceName ? deviceDetails!.deviceName : deviceDetails!.name}
          setShowRenameDeviceModal={setShowRenameDeviceModal}
          renameUserDevice={renameUserDevice}
        />
      )}
    </>
  )
}

export default DevicesHeader
